import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../redux/store";
import { loginSuccess } from "../redux/authSlice";
import { trackEvent } from "../services/analytics";
import LocationCapture from "./LocationCapture";
import PreferencesSetup from "./PreferencesSetup";
import api from "../services/api";

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [status, setStatus] = useState<"loading" | "location" | "payment" | "preferences" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [userLocation, setUserLocation] = useState<any>(null);
  
  // Determine correct API URL based on environment
  const getApiBaseUrl = () => {
    return process.env.NODE_ENV === "production"
      ? (process.env.REACT_APP_API_BASE_URL || "https://spacepings.space")
      : (process.env.REACT_APP_API_BASE_URL || "http://localhost:5000");
  };

  useEffect(() => {
    const processAuth = async () => {
      try {
        // Extract token and email from query parameters
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get("token");
        const email = searchParams.get("email");
        const provider = searchParams.get("provider") || "oauth";

        if (!token) {
          const error = "No authentication token received";
          console.error(error);
          trackEvent(
            "login_failed",
            JSON.stringify({
              method: provider,
              error,
              timestamp: new Date().toISOString(),
            }),
          );

          setStatus("error");
          setErrorMessage(error);

          setTimeout(() => {
            navigate("/auth?error=" + encodeURIComponent(error));
          }, 1000);

          return;
        }

        // Store token and email in local storage
        localStorage.setItem("token", token);
        if (email) {
          localStorage.setItem("email", email);
        }

        // Dispatch login success to Redux
        dispatch(loginSuccess({ token, email: email || "" }));

        // Track successful login event
        trackEvent(
          "login_success",
          JSON.stringify({
            method: provider,
            email: email ? email.substring(0, 3) + "***" : "anonymous",
            timestamp: new Date().toISOString(),
          }),
        );

        // Check for returnTo parameter in the OAuth callback URL (passed from backend)
        const returnTo = searchParams.get('returnTo');
        const actionParam = searchParams.get('action');
        
        // Check for pending checkout data first (highest priority)
        let pendingCheckout = localStorage.getItem('pendingCheckout');
        
        // If main key is missing, try backup keys
        if (!pendingCheckout) {
          pendingCheckout = localStorage.getItem('pendingCheckout_backup') || 
                          localStorage.getItem('authflow_pricing');
        }
        
        if (pendingCheckout) {
          try {
            const checkoutData = JSON.parse(pendingCheckout);
            const { plan, billing } = checkoutData;
            
            // Clean up all checkout-related localStorage keys
            localStorage.removeItem('pendingCheckout');
            localStorage.removeItem('pendingCheckout_backup');
            localStorage.removeItem('authflow_pricing');
            
            console.log('Found pending checkout, redirecting to checkout with:', { plan, billing });
            trackEvent(
              "checkout_resume",
              JSON.stringify({
                plan,
                billing,
                method: provider,
                returnTo,
                action: actionParam,
                timestamp: new Date().toISOString(),
              }),
            );
            navigate(`/checkout?plan=${plan}&billing=${billing}`);
            return;
          } catch (err) {
            console.error('Error parsing pending checkout data:', err);
            // Clean up corrupted data
            localStorage.removeItem('pendingCheckout');
            localStorage.removeItem('pendingCheckout_backup');
            localStorage.removeItem('authflow_pricing');
          }
        }
        
        // If we have returnTo=/pricing and action=checkout but no pendingCheckout,
        // try to extract plan/billing from URL params as a fallback
        if (returnTo === '/pricing' && actionParam === 'checkout') {
          console.log('Pricing flow detected but no pending checkout');
          
          // Check for plan/billing parameters in the callback URL
          const planParam = searchParams.get('plan');
          const billingParam = searchParams.get('billing');
          
          if (planParam && billingParam) {
            console.log('Found plan/billing in URL params, proceeding to checkout');
            trackEvent(
              "checkout_resume_from_url",
              JSON.stringify({
                plan: planParam,
                billing: billingParam,
                method: provider,
                timestamp: new Date().toISOString(),
              }),
            );
            navigate(`/checkout?plan=${planParam}&billing=${billingParam}`);
            return;
          }
          
          console.log('No plan/billing found, redirecting to pricing page for reselection');
          trackEvent(
            "checkout_redirect_to_pricing",
            JSON.stringify({
              method: provider,
              returnTo,
              action: actionParam,
              timestamp: new Date().toISOString(),
            }),
          );
          navigate('/pricing?auth_complete=true');
          return;
        }

        // Check if this is a returning user with an active subscription
        try {
          const API_BASE_URL = getApiBaseUrl();
          const subscriptionResponse = await fetch(`${API_BASE_URL}/api/subscription/status`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (subscriptionResponse.ok) {
            const subscriptionData = await subscriptionResponse.json();
            console.log('Subscription data:', subscriptionData);
            
            // Check if this is a dev account
            const isDevAccount = localStorage.getItem('is_dev_account') === 'true';
            
            if (isDevAccount) {
              // Dev account - always start with location capture to test the flow
              console.log('Dev account detected, starting test flow with location capture');
              trackEvent(
                "dev_account_test_flow",
                JSON.stringify({
                  timestamp: new Date().toISOString(),
                }),
              );
              setStatus("location");
            } else if (subscriptionData.active && subscriptionData.plan) {
              // Returning user with active subscription - go to dashboard
              console.log('Returning user with active subscription, redirecting to dashboard');
              trackEvent(
                "returning_user_login",
                JSON.stringify({
                  plan: subscriptionData.plan,
                  has_starlink: subscriptionData.has_starlink_addon,
                  timestamp: new Date().toISOString(),
                }),
              );
              navigate("/dashboard");
            } else {
              // New user or user without subscription - start sign-up flow
              console.log('New user, starting sign-up flow with location capture');
              trackEvent(
                "new_user_signup_flow",
                JSON.stringify({
                  timestamp: new Date().toISOString(),
                }),
              );
              setStatus("location");
            }
          } else if (subscriptionResponse.status === 401) {
            // Invalid token - redirect to auth
            console.log('Invalid token, redirecting to auth');
            localStorage.removeItem("token");
            navigate("/auth?error=" + encodeURIComponent("Session expired"));
          } else {
            // User may not exist - start sign-up flow
            console.log('User may not exist, starting sign-up flow');
            setStatus("location");
          }
        } catch (err) {
          // In case of any errors, start sign-up flow
          console.error('Error checking subscription status:', err);
          setStatus("location");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Authentication error occurred";
        console.error("Auth callback error:", errorMessage);

        trackEvent(
          "login_error",
          JSON.stringify({
            error: errorMessage,
            timestamp: new Date().toISOString(),
          }),
        );

        setStatus("error");
        setErrorMessage(errorMessage);

        setTimeout(() => {
          navigate("/auth?error=" + encodeURIComponent(errorMessage));
        }, 1000);
      }
    };

    processAuth();
  }, [navigate, location.search, dispatch]);

  const handleLocationSet = async (location: { city: string; latitude: number; longitude: number }) => {
    try {
      setUserLocation(location);
      
      // Update user location in backend
      const token = localStorage.getItem("token");
      if (token) {
        await api.put('/api/user/location', location, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      trackEvent('location_set', JSON.stringify(location));
      
      // Check if this is a dev account
      const isDevAccount = localStorage.getItem('is_dev_account') === 'true';
      
      if (isDevAccount) {
        // Dev account - skip payment, go to preferences to test the flow
        console.log('Dev account - skipping payment, going to preferences');
        trackEvent('dev_account_skip_payment', JSON.stringify({ location }));
        navigate('/preferences-setup');
        return;
      }
      
      // Check for pending checkout data to determine pricing
      const pendingCheckout = localStorage.getItem('pendingCheckout');
      if (pendingCheckout) {
        try {
          const checkoutData = JSON.parse(pendingCheckout);
          navigate(`/checkout?plan=${checkoutData.plan}&billing=${checkoutData.billing}`);
          return;
        } catch (err) {
          console.error('Error parsing pending checkout:', err);
        }
      }
      
      // Default to pricing page
      navigate('/pricing?new_user=true');
      
    } catch (err) {
      console.error('Error setting location:', err);
      // Continue to pricing even if location update fails
      navigate('/pricing?new_user=true');
    }
  };

  const handlePreferencesComplete = () => {
    const isDevAccount = localStorage.getItem('is_dev_account') === 'true';
    
    trackEvent('preferences_completed', JSON.stringify({ is_dev_account: isDevAccount }));
    
    if (isDevAccount) {
      console.log('Dev account preferences completed - going to dashboard with full access');
    }
    
    navigate('/dashboard');
  };

  if (status === "location") {
    return <LocationCapture onLocationSet={handleLocationSet} />;
  }

  if (status === "preferences") {
    return <PreferencesSetup onComplete={handlePreferencesComplete} />;
  }

  if (status === "error") {
    return (
      <div className="auth-error">
        <style>{`
          .auth-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            padding: 20px;
            text-align: center;
            background: radial-gradient(circle at center, #0a0e23 0%, #000000 100%);
            color: #ffffff;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            overflow: hidden;
          }
          
          .error-icon {
            font-size: 3rem;
            color: #EF4444;
            margin-bottom: 1rem;
            animation: pulse 2s infinite;
          }
          
          .error-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            text-shadow: 0 0 10px rgba(239, 68, 68, 0.5);
          }
          
          .error-message {
            max-width: 400px;
            margin-bottom: 1.5rem;
            opacity: 0.9;
            font-size: 1rem;
            line-height: 1.5;
          }
          
          .return-button {
            padding: 0.75rem 1.5rem;
            background: linear-gradient(45deg, #6366F1, #8B5CF6);
            border: none;
            border-radius: 12px;
            color: #ffffff;
            font-weight: 500;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
          }
          
          .return-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.5);
            background: linear-gradient(45deg, #4f46e5, #7c3aed);
          }

          .stars-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: -1;
            pointer-events: none;
          }

          .star {
            position: absolute;
            background: #ffffff;
            border-radius: 50%;
            opacity: 0.4;
            animation: twinkle linear infinite;
          }

          @keyframes twinkle {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.8; }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.7; }
            50% { transform: scale(1.2); opacity: 1; }
          }
        `}</style>
        <div className="stars-container">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDuration: `${Math.random() * 3 + 2}s`,
                animationDelay: `${Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
        <div className="error-icon">⚠️</div>
        <h2 className="error-title">Authentication Failed</h2>
        <p className="error-message">{errorMessage}</p>
        <button className="return-button" onClick={() => navigate("/auth")}>
          Return to Login
        </button>
      </div>
    );
  }

  return (
    <div className="auth-loading">
      <style>{`
        .auth-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          padding: 20px;
          text-align: center;
          background: radial-gradient(circle at center, #0a0e23 0%, #000000 100%);
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow: hidden;
        }
        
        .loader {
          width: 60px;
          height: 60px;
          border: 5px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          border-top-color: #6366F1;
          animation: spin 1s ease-in-out infinite, glow 2s ease-in-out infinite;
          margin-bottom: 1.5rem;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3); }
          50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.6); }
        }
        
        .loading-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          text-shadow: 0 0 10px rgba(147, 112, 219, 0.5);
        }
        
        .loading-message {
          max-width: 400px;
          opacity: 0.9;
          font-size: 1rem;
          line-height: 1.5;
        }
        
        .stars-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          pointer-events: none;
        }
        
        .star {
          position: absolute;
          background: #ffffff;
          border-radius: 50%;
          opacity: 0.4;
          animation: twinkle linear infinite;
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }

        .nebula {
          position: absolute;
          background: radial-gradient(circle, rgba(147, 112, 219, 0.3), transparent);
          border-radius: 50%;
          opacity: 0.5;
          animation: nebula-glow 10s ease-in-out infinite;
        }

        @keyframes nebula-glow {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 0.8; }
        }
      `}</style>

      <div className="stars-container">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="star"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${Math.random() * 3 + 2}s`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
        <div
          className="nebula"
          style={{
            top: "20%",
            left: "30%",
            width: "100px",
            height: "100px",
            animationDelay: "0s",
          }}
        />
        <div
          className="nebula"
          style={{
            top: "60%",
            right: "20%",
            width: "80px",
            height: "80px",
            animationDelay: "2s",
          }}
        />
      </div>

      <div className="loader" aria-hidden="true"></div>
      <h2 className="loading-title">Authenticating</h2>
      <p className="loading-message">
        Please wait while we prepare your cosmic journey...
      </p>
    </div>
  );
};

export default AuthCallback;
